import { FormControl } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { Image } from '../../../shared/shared.interface';

export interface KngFilter {
  state: KngFilterState;
}

export interface FilterJSON {
  heading: Heading;
  sections: Section[];
  ctaList: Cta[];
}

export interface KngFilterState {
  filterData: FilterJSON;
  hidden: boolean;
}

export interface KngFilterOutputState {
  id: string;
  state: string;
}

interface Heading {
  label: string;
}

export interface Section {
  label: string;
  collapsed: boolean;
  controls: Control[];
  selectionStateControls?: Control[];
  isShowMorePresent?: boolean;
  showMoreShowLess?: {
	isShowMoreClicked: boolean;
	isDisabled: boolean;
	showMoreText: string;
	showLessText: string;
  };
  noResultsFoundText?: string;
  noAssociatedResultsFoundText?: string;
  accordionExpandedIcon: Image;
  accordionCollapsedIcon: Image;
  selectedValue?: string[];
  searchIcon: Image;
  crossIcon: Image;
  hidden?: boolean;
  errorMessage?: string;
  isAPIError?: boolean;
}

export interface Option {
  label: string;
  description?: string;
  id?: string;
  value: string;
  checked?: boolean;
}

export interface Cta {
  id: string;
  label: string;
  className: string;
  disabled: boolean;
}

export interface CtaClickEvent {
  event: Event;
  buttonClicked: Cta;
  state: KngFilterState;
}

export enum ControlType {
  radio = 'radio',
  input = 'input',
  checkbox = 'checkbox',
  none = 'none',
  notDate = 'Not Date'
}

export enum DateRange {
  lastDay = 'Last Day',
  lastSevenDays = 'Last 7 Days',
  lastThirtyDays = 'Last 30 Days',
  lastSixtyDays = 'Last 60 Days (default)',
  lastQuarter = 'Last Quarter',
  lastYear = 'Last Year',
  lastYearDefault = 'Last Year (default)',
  last2YearsDefault = 'Last 2 Years (default)',
  custom = 'Custom Date Range',
  lastSixtyDaysWithoutDefault = 'Last 60 Days',
  nextSevenDays = 'Next 7 days',
  nextThirtyDays = 'Next 30 days (default)',
  nextSixtyDays = 'Next 60 days',
  nextQuarter = 'Next Quarter',
  nextYear = 'Next Year'
}

export enum TermLabel {
	other = 'Other'
}

export enum DateRangeId {
	lastDay = '846d0c4b-3882-4ab2-a508-e2401d1ee040',
	lastSevenDays = 'f0fc4edf-e966-4f7b-9f8b-8d30bb51d115',
	lastThirtyDays = '07e102cf-a4d9-4697-9fa4-81756fc08b61',
	lastSixtyDaysDefault = '481d774c-ddd7-404d-8011-3c3f6145bb96',
	lastQuarter = '44d4a1aa-d18d-4584-809e-065d9cab66c0',
	lastYear = 'db878879-283b-4066-9461-51e6c38d703c',
	custom = '9efa7309-517a-4513-b9a2-01123e91c271',
	lastYearDefault = 'd2f53022-98ca-4944-91e7-8c4f11eae1c4',
    lastSixtyDaysWithoutDefault = '8488fbea-e030-4049-b2ed-20fe167634e5'
}

export enum CalendarFilter {
	monthsLabel = 'Months',
	toLabel = 'To',
	fromDateError = 'From date must be before the To date',
	toDateError = 'To date must be after the From date',
	fiveYear = 1825,
	next = 'Next'
}

export interface ControlOptions {
  id: string;
  label: string;
  type: ControlType;
  value: unknown;
  inputPlaceholderText: string;
  inputIcon: string;
  isLocation?:boolean;
  address?:Address;
  options: Option[];
  quoteKeys?: string[];
  isHidden?: boolean;
}
export class Control extends FormControl 
{
	private controlOptions$: BehaviorSubject<ControlOptions> =
		new BehaviorSubject<ControlOptions>({
			id: '',
			label: '',
			type: ControlType.none,
			inputPlaceholderText: '',
			inputIcon: '',
			options: [],
			value: '',
			isLocation: false,
			isHidden: false
		});
	get id(): string 
	{
		return this.controlOptions$.value.id;
	}
	get label(): string 
	{
		return this.controlOptions$.value.label;
	}
	get type(): ControlType 
	{
		return this.controlOptions$.value.type;
	}
	get inputPlaceholderText(): string 
	{
		return this.controlOptions$.value.inputPlaceholderText;
	}
	get inputIcon(): string 
	{
		return this.controlOptions$.value.inputIcon;
	}
	get options(): Option[] 
	{
		return this.controlOptions$.value.options;
	}
	get isLocation(): boolean|undefined  
	{
		return this.controlOptions$.value.isLocation;
	}
	get address(): Address|undefined 
	{
		return this.controlOptions$.value.address;
	}
	get quoteKeys(): string[]|undefined 
	{
		return this.controlOptions$.value.quoteKeys;
	}
	get isHidden(): boolean|undefined  
	{
		return this.controlOptions$.value.isHidden;
	}
	set type(value: ControlType) 
	{
		this.controlOptions$.value.type = value;
	}
	set options(value: Option[]) 
	{
		this.controlOptions$.value.options = value;
	}
	set inputPlaceholderText(value: string) 
	{
		this.controlOptions$.value.inputPlaceholderText = value;
	}
	set inputIcon(value: string) 
	{
		this.controlOptions$.value.inputIcon = value;
	}
	set isHidden(value: boolean | undefined) 
	{
		this.controlOptions$.value.isHidden = value;
	}
	constructor(options: ControlOptions) 
	{
		super();
		this.setValue(options.value);
		this.controlOptions$.next(options);
	}
}
export interface Address{
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  country?:string;
  zipCode: string;
  zip?: string;
}
